import { useState } from 'react'
import { Box, Button, Card, CardActionArea, Chip, Grid, Typography } from '@mui/material'
import { MdAccessTime, MdCheck, MdStar } from 'react-icons/md'
import { MarketplaceThemeType } from '../../utils/sharedTypes'
import { WALTER_COLORS } from '../../utils/artDirection'
import {
  formatBudgetLabel,
  formatDuration,
  formatPrice,
  formatRating,
  organismKey,
  productCardImageUrl,
  productLogotypeUrl,
} from './marketplaceAssets'

// Au-delà de trois badges, la card se noie dans les mentions de financement.
const MAX_VISIBLE_FUNDERS = 3

type ThemeCardPropsType = {
  theme: MarketplaceThemeType
  inCart: boolean
  /** Formation déjà rattachée à une inscription du groupe : on n'en propose pas une seconde. */
  alreadyRegistered: boolean
  onOpen: () => void
  onToggleCart: () => void
  /** Card large mise en avant : image plus haute et accroche développée. */
  featured?: boolean
  /** Formation financée entièrement par le financeur imposé (IPERIA) : pas de prix à afficher. */
  hidePrice?: boolean
}

const ThemeCard = ({
  theme,
  inCart,
  alreadyRegistered,
  onOpen,
  onToggleCart,
  featured = false,
  hidePrice = false,
}: ThemeCardPropsType) => {
  const [imageFailed, setImageFailed] = useState(false)
  const [logotypeFailed, setLogotypeFailed] = useState(false)

  const product = theme.default_product
  const productCode = product?.product_code
  const price = hidePrice ? null : formatPrice(product?.listing_price)
  const duration = formatDuration(product?.duration)
  const rating = formatRating(product?.ratings_avg, product?.ratings_count)
  const title = theme.title || theme.theme_code
  const subtitle = theme.selling_point || product?.selling_point || theme.description
  const visibleFunders = theme.eligible_funding_organisms.slice(0, MAX_VISIBLE_FUNDERS)
  const hiddenFundersCount = theme.eligible_funding_organisms.length - visibleFunders.length

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: inCart ? 'text.primary' : undefined,
        borderWidth: inCart ? 2 : 1,
        // La direction artistique bannit les ombres : le survol se signale par un léger décalage.
        transition: 'transform 150ms ease, border-color 150ms ease',
        '&:hover': { transform: 'translateY(-3px)', borderColor: 'text.primary' },
      }}
    >
      <CardActionArea
        onClick={onOpen}
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: featured ? 260 : 190,
            // Neutre chaud visible quand l'image S3 n'existe pas pour ce produit : la couleur d'accent
            // est réservée à l'action, elle ne sert pas de remplissage.
            backgroundColor: WALTER_COLORS.mastic,
            overflow: 'hidden',
          }}
        >
          {productCode && !imageFailed && (
            <Box
              component="img"
              src={productCardImageUrl(productCode)}
              alt=""
              onError={() => setImageFailed(true)}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          )}

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 35%, rgba(37,40,49,0.92) 100%)',
            }}
          />

          {(inCart || alreadyRegistered) && (
            <Chip
              size="small"
              color={alreadyRegistered ? 'default' : 'primary'}
              icon={<MdCheck />}
              label={alreadyRegistered ? 'Déjà dans vos inscriptions' : 'Dans mon panier'}
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                fontWeight: 600,
                backgroundColor: alreadyRegistered ? 'rgba(255,255,255,0.92)' : undefined,
              }}
            />
          )}

          {rating && (
            <Chip
              size="small"
              icon={<MdStar />}
              label={rating.label}
              sx={{ position: 'absolute', top: 12, right: 12, backgroundColor: 'rgba(255,255,255,0.92)' }}
            />
          )}

          {productCode && !logotypeFailed ? (
            <Box
              component="img"
              src={productLogotypeUrl(productCode)}
              alt={title}
              onError={() => setLogotypeFailed(true)}
              sx={{ position: 'absolute', bottom: 16, left: 0, width: '100%', px: 2, height: 'auto', maxHeight: 56 }}
            />
          ) : (
            <Typography
              variant={featured ? 'h5' : 'h6'}
              sx={{ position: 'absolute', bottom: 16, left: 0, px: 2, color: 'common.white' }}
            >
              {title}
            </Typography>
          )}
        </Box>

        <Box sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: 'text.secondary',
                display: '-webkit-box',
                WebkitLineClamp: featured ? 3 : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {subtitle}
            </Typography>
          )}

          <Grid container spacing={0.75} sx={{ mt: 1.5 }}>
            {visibleFunders.map((organism) => {
              const budget = formatBudgetLabel(organism)
              return (
                <Grid item key={organismKey(organism)}>
                  {/* Puces en neutre chaud, comme les étiquettes des sites vitrines. */}
                  <Chip
                    size="small"
                    label={budget ? `${organism.code || organism.name} · ${budget}` : organism.code || organism.name}
                  />
                </Grid>
              )
            })}
            {hiddenFundersCount > 0 && (
              <Grid item>
                <Chip size="small" label={`+${hiddenFundersCount}`} />
              </Grid>
            )}
          </Grid>

          <Grid container alignItems="center" spacing={1.5} sx={{ mt: 'auto', pt: 2 }}>
            {duration && (
              <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                <MdAccessTime size={16} />
                <Typography variant="body2">{duration}</Typography>
              </Grid>
            )}
            {price && (
              <Grid item>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {price}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </CardActionArea>

      <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1 }}>
        <Button
          fullWidth
          variant={inCart ? 'outlined' : 'contained'}
          disabled={!product || alreadyRegistered}
          onClick={onToggleCart}
          startIcon={inCart ? <MdCheck /> : undefined}
        >
          {alreadyRegistered ? 'Déjà inscrit' : inCart ? 'Retirer' : 'Ajouter'}
        </Button>
        <Button fullWidth variant="text" onClick={onOpen}>
          Découvrir
        </Button>
      </Box>
    </Card>
  )
}

export default ThemeCard
